import { useState } from '@hookstate/core';

import { Icon as BlueIcon } from '@blueprintjs/core';

import { DeleteDialog, GlobalMenu } from '@components';

import { useMutation } from 'react-query';
import { deleteEquipment } from '@services/queries/equipments';
import { useHistory } from 'react-router-dom';
import { formatDate, getWarrantyStatus } from '@utils';
import { SetStateAction, useContext, useRef } from 'react';
import { AssetContext } from '@pages/Asset/Asset.query';
import { useAppDispatch } from '@store/redux/store';
import { removeRecentViewed } from '@store/redux/features/search';
import { SEARCH_INVENTORY_KEY } from '@pages/Products/Products';
import copyToClipboard from 'copy-to-clipboard';
import { InviteUserRole } from '@models/Invite.model';
import { NavigationContext } from '@layout/navigation/navigation';
import { trackAssetDeleted } from '@services/mixpanel/trackers/AssetMetrics';
import { getProductValue } from '@utils/getProductValue';
import { isImmediateNonNullable } from '@utils/isNonNullable';
import { IRefGlobalMenu } from '@components/GlobalMenu/GlobalMenu';
import { useSelector } from 'react-redux';
import { checkDevice } from '@store/redux/features/mobile/mobileSelectors';
import {
  Container,
  Icon12,
  Icon16,
  MatchBottom,
  MatchChange,
  MatchContainer,
  MatchDescription,
  MatchTitle,
  MatchTop,
} from './SideBarMenu.styles';
import { CloneAsset } from '..';

interface SideBarMenu {
  isUnidentified?: boolean;
  setOpenManualEdit: (value: SetStateAction<boolean>) => void;
}

const SideBarMenu = ({ setOpenManualEdit, isUnidentified }: SideBarMenu) => {
  const { equipmentQuery } = useContext(AssetContext);
  const { role } = useContext(NavigationContext);
  const history = useHistory();
  const dispatch = useAppDispatch();

  const globlaMenuRef = useRef<IRefGlobalMenu>(null);
  const { isDesktop } = useSelector(checkDevice());

  const openDeleteDialog = useState(false);
  const openCloneAsset = useState<boolean>(false);
  const equipment = equipmentQuery?.data;

  const { product } = equipment || {};

  const productData = getProductValue(equipment);

  const mutationDeleteEquipment = useMutation(deleteEquipment, {
    onSuccess: () => {
      if (equipment && isImmediateNonNullable(productData)) {
        const { manufacturerName, model, name } = productData;
        trackAssetDeleted(name, model, manufacturerName, equipment.id);
      }
      history.push('/products');
    },
  });

  if (!equipment) {
    return <div />;
  }
  const { model, name, manufacturerName } = productData;

  const warranty = getWarrantyStatus(
    equipment?.purchasedDate,
    product?.standardWarrantyLength,
  );

  const getWarranty = () => {
    if (!warranty.daysNumber) {
      return 'no data available';
    }
    if (warranty.daysNumber >= 0) {
      return 'Active';
    }
    return 'Expired';
  };

  const topLabel = (): (() => JSX.Element) | undefined => {
    if (!isUnidentified) {
      return undefined;
    }

    return () => (
      <MatchContainer
        onClick={() => {
          setOpenManualEdit(true);
          if (globlaMenuRef.current) {
            globlaMenuRef.current.toggleMenu(false);
          }
        }}
      >
        <MatchTop>
          <Icon12>
            <BlueIcon icon="error" color="#8F95B2" size={12} />
          </Icon12>
          <MatchTitle>Unidentified</MatchTitle>
        </MatchTop>
        <MatchDescription>6 Suggestions Available</MatchDescription>
        <MatchBottom>
          <MatchChange>Change the Match</MatchChange>
          <Icon16>
            <BlueIcon icon="arrow-right" color="#8F95B2" />
          </Icon16>
        </MatchBottom>
      </MatchContainer>
    );
  };

  const copyAsset = {
    label: 'Copy Asset Data',
    iconName: 'clipboard',
    toolTipText: 'Copied!',
    toolTipId: 'copyAssetData',
    hideAfterShow: true,
    toolTipEvent: 'click',
    stillOpenAfterClick: true,
    onClick: () => {
      copyToClipboard(
        `Serial #: ${
          equipment?.serial ? equipment?.serial : 'no data available'
        }\nPurchase From: ${
          equipment?.purchasedFrom
            ? equipment?.purchasedFrom
            : 'no data available'
        }\nSLA: ${
          equipment?.sla ? equipment?.sla : 'no data available'
        }\nInstalled Firmware Version: ${
          equipment?.installedFirmwareVersion
            ? equipment?.installedFirmwareVersion
            : 'no data available'
        }\nInstalled Software Version: ${
          equipment?.installedSoftwareVersion
            ? equipment?.installedSoftwareVersion
            : 'no data available'
        }\nPurchased Date: ${
          equipment?.purchasedDate
            ? formatDate(equipment?.purchasedDate)
            : 'no data available'
        }\nProduct Name: ${name || 'no data available'}\nModel #: ${
          model || 'no data available'
        }\nManufacturer: ${
          manufacturerName || 'no data available'
        }\nWarranty: ${getWarranty()}`,
      );
    },
  };
  const cloneAsset = {
    label: 'Clone Asset',
    iconName: 'applications',
    onClick: () => {
      openCloneAsset.set(true);
    },
  };
  const deleteAsset = {
    disabled: ![InviteUserRole.ADMIN, InviteUserRole.EDITOR].includes(role),
    disableToolTip: [InviteUserRole.ADMIN, InviteUserRole.EDITOR].includes(
      role,
    ),
    toolTipText: 'Only admins and editors can delete an asset.',
    toolTipId: 'deleteTooltip',
    label: 'Delete Asset',
    iconName: 'trash',
    onClick: () => {
      openDeleteDialog.set(true);
    },
  };
  function getItems() {
    if (isDesktop) return [copyAsset, cloneAsset, deleteAsset];
    return [copyAsset, deleteAsset];
  }
  return (
    <Container className="reset-global">
      <GlobalMenu
        ref={globlaMenuRef}
        align="end"
        title="Asset Options"
        TopLabel={topLabel()}
        itemList={getItems()}
      />

      <DeleteDialog
        open={openDeleteDialog}
        onDelete={() => {
          mutationDeleteEquipment.mutate(equipment.id);
          dispatch(
            removeRecentViewed({
              keyHistory: SEARCH_INVENTORY_KEY,
              value: {
                link: `/equipments/${equipment.id}`,
                labelOne: '',
                thumbnail: undefined,
                labelTwo: undefined,
              },
            }),
          );
        }}
        width="min(calc(100% - 16px), 379px)"
      />
      <CloneAsset
        open={openCloneAsset.get()}
        onClose={() => openCloneAsset.set(false)}
        openCloneAsset={openCloneAsset}
      />
    </Container>
  );
};

export { SideBarMenu };
